// Global
import React from 'react';

// Local
import { MixedMediaGrid } from './MixedMediaGrid';
import { CardListing } from '@generated/CardListing/MixedMediaGrid.model';
import { PlaceholderWrapper } from 'components/helpers/SitecoreWrappers/PlaceholderWrapper/PlaceholderWrapper';
import { Themes } from 'lib/themes';
import { Item } from '@sitecore-content-sdk/nextjs';
import { usePreambleFields } from 'components/helpers/Molecules/Preamble/Preamble.sitecore';
import styles from '../CardListing.module.scss';
import clsx from 'clsx';
import { ContentGridParams } from '../ContentGrid/ContentGrid.sitecore';
import { CardFields } from '../CardListingShared';
import useIsEditing from 'lib/hooks/sitecore/useIsEditing';
import { EditingHelpText } from 'components/helpers/Editing/EditingHelpText';
import { getEnumValue } from 'lib/utils/sitecore/enum';
import { ImageAspectRatio } from 'components/helpers/Atoms/EnhancedNextImage/ImageCommon';
import { fieldTypes } from '../CardListingShared';
import { useContentGridSettings, useDefaultCardDetailLevel } from 'lib/hooks/useSiteSettings';
import { PreambleOrientation } from 'components/helpers/Molecules/Preamble/Preamble';
import { ContentGridCardAlignment } from '../ContentGrid/ContentGrid';

type ColCount = '1' | '2' | '3' | '4' | undefined;

type MixedMediaGridProps = Omit<CardListing.MixedMediaGrid.MixedMediaGrid_Component, 'params'> & {
  params?: ContentGridParams & {
    DynamicPlaceholderId: string;
  };
};

const MixedDynamic = (props: MixedMediaGridProps) => {
  const { DynamicPlaceholderId } = props?.params || {};
  const phKey = DynamicPlaceholderId
    ? `custom-headless-grid-${DynamicPlaceholderId}`
    : 'custom-headless-grid';
  const colCount = (props.params?.numColumns || '4') as ColCount;

  const contentGridSettings = useContentGridSettings();

  // Get the default card detail level from site settings (handles both flat and nested shapes)
  const defaultCardDetailLevel = useDefaultCardDetailLevel();

  // Priority: 1) cardDetails from component params, 2) defaultCardDetailLevel from site settings, 3) all fields
  const fieldsToShow = props.params?.cardDetails?.fieldsToShow?.length
    ? props.params.cardDetails.fieldsToShow.map((x: Item) => x.name as CardFields)
    : defaultCardDetailLevel?.length
      ? defaultCardDetailLevel.map((x) => x.name as CardFields)
      : [...fieldTypes];

  const preambleFields = usePreambleFields({
    fields: props.fields,
    ctaComponentClassOverrides: {
      cta1: 'component-content-grid-color-button-color-1',
      cta2: 'component-content-grid-color-button-color-2',
    },
  });

  const isEditing = useIsEditing();

  const defaultAspectRatio = getEnumValue<ImageAspectRatio>(
    contentGridSettings?.fields?.defaultCardImageAspectRatio
  );
  const gridAspectRatio =
    getEnumValue<ImageAspectRatio>(props.params?.gridAspectRatio) ?? defaultAspectRatio;

  const result = (
    <MixedMediaGrid
      {...preambleFields}
      theme={props.params?.theme as Themes}
      numColumns={colCount}
      preambleOrientation={props.params?.preambleOrientation as PreambleOrientation}
      centerLastRow={props.params?.centerLastRow === '1'}
      constrainedComponentWidth={props.params?.constrainedComponentWidth === '1'}
      content={
        <PlaceholderWrapper
          name={phKey}
          rendering={props.rendering}
          fieldsToShow={fieldsToShow}
          gridAspectRatio={gridAspectRatio}
          cardAlignment={props.params?.cardAlignment as ContentGridCardAlignment}
          render={(components) => (
            <ul
              className={clsx(styles.contentGridContent, styles[`col-${colCount}`], {
                [styles.centerLastRow]: props.params?.centerLastRow,
              })}
            >
              {components.map((comp, index) => (
                <li
                  key={index}
                  id={index.toString()}
                  className={clsx(styles.contentGridCard, styles[`col-${colCount}`])}
                >
                  {comp}
                </li>
              ))}
            </ul>
          )}
        />
      }
    />
  );

  if (isEditing) {
    const hiddenFields = fieldTypes.filter((field) => !fieldsToShow.includes(field));
    const cardDetailsSource = props.params?.cardDetails?.fieldsToShow?.length
      ? 'Card Details (Component Level)'
      : defaultCardDetailLevel?.length
        ? 'Default Card Detail Level (Site Settings)'
        : 'All Fields (Default Fallback)';
    return (
      <div>
        <EditingHelpText>Card configuration source: {cardDetailsSource}</EditingHelpText>
        <EditingHelpText>Shown fields: {fieldsToShow.join(', ')}</EditingHelpText>
        <EditingHelpText>Hidden fields: {hiddenFields.join(', ')}</EditingHelpText>
        {result}
      </div>
    );
  }
  return result;
};

export const Default = MixedDynamic;
